import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ThemeService from '../services/ThemeService';

export interface TenantTheme {
  displayName: string;
  branding: {
    logoUrl: string;
    faviconUrl: string;
    colors: {
      primary: string;
      secondary: string;
      accent?: string;
      background?: string;
      text?: string;
    };
    typography?: {
      fontFamily: string;
      headingFont?: string;
    };
  };
  customization: {
    showWelcomeMessage: boolean;
    welcomeMessage?: string;
    footerText?: string;
    supportEmail?: string;
    supportPhone?: string;
  };
}

export interface TenantContextType {
  tenantKey: string | null;
  displayName: string;
  theme: TenantTheme | null;
  socketNamespace: string;
  isLoading: boolean;
  error: string | null;
  setTenant: (tenantKey: string, theme?: TenantTheme) => void;
  clearTenant: () => void;
  updateTheme: (theme: TenantTheme) => void;
}

const defaultTheme: TenantTheme = {
  displayName: 'Colegio Sin Notas',
  branding: {
    logoUrl: '/images/logo.png',
    faviconUrl: '/favicon.svg',
    colors: {
      primary: '#3B82F6',
      secondary: '#10B981',
      accent: '#F59E0B',
      background: '#FFFFFF',
      text: '#1F2937'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif'
    }
  },
  customization: {
    showWelcomeMessage: true,
    welcomeMessage: 'Bienvenido al Sistema de Evaluación Integral',
    footerText: 'Sistema de Evaluación Integral',
    supportEmail: 'soporte@colegio.cl',
    supportPhone: '+56 9 1234 5678'
  }
};

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantKey, setTenantKey] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('Colegio Sin Notas');
  const [theme, setTheme] = useState<TenantTheme | null>(defaultTheme);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Get ThemeService instance
  const themeService = ThemeService.getInstance();

  // Compute socket namespace based on tenant key
  const socketNamespace = tenantKey ? `/ws/${tenantKey}` : '/ws/default';

  /**
   * Load theme configuration for a tenant using ThemeService
   */
  const loadTenantTheme = async (tenantKey: string): Promise<TenantTheme> => {
    try {
      setIsLoading(true);
      setError(null);

      const theme = await themeService.loadTenantTheme(tenantKey, {
        useCache: true,
        fallbackToDefault: true
      });

      return theme;

    } catch (error: any) {
      console.error('Error loading tenant theme:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Apply theme to document using ThemeService
   */
  const applyTheme = (theme: TenantTheme) => {
    themeService.applyTheme(theme);
  };

  /**
   * Set tenant and load its theme
   */
  const setTenant = async (newTenantKey: string, providedTheme?: TenantTheme) => {
    try {
      setTenantKey(newTenantKey);
      
      let tenantTheme: TenantTheme;
      
      if (providedTheme) {
        tenantTheme = providedTheme;
      } else {
        tenantTheme = await loadTenantTheme(newTenantKey);
      }
      
      setTheme(tenantTheme);
      setDisplayName(tenantTheme.displayName);
      applyTheme(tenantTheme);
      
      // Store tenant info in localStorage for persistence
      localStorage.setItem('tenantKey', newTenantKey);
      localStorage.setItem('tenantTheme', JSON.stringify(tenantTheme));
      
      console.log(`✅ Tenant set: ${newTenantKey} (${tenantTheme.displayName})`);
      
    } catch (error: any) {
      console.error('Error setting tenant:', error);
      setError(error.message);
    }
  };

  /**
   * Clear tenant context
   */
  const clearTenant = () => {
    setTenantKey(null);
    setDisplayName('Colegio Sin Notas');
    setTheme(defaultTheme);
    setError(null);
    
    // Apply default theme
    applyTheme(defaultTheme);
    
    // Clear from localStorage
    localStorage.removeItem('tenantKey');
    localStorage.removeItem('tenantTheme');
    
    console.log('🧹 Tenant context cleared');
  };

  /**
   * Update theme without changing tenant
   */
  const updateTheme = (newTheme: TenantTheme) => {
    setTheme(newTheme);
    setDisplayName(newTheme.displayName);
    applyTheme(newTheme);
    
    // Update localStorage
    if (tenantKey) {
      localStorage.setItem('tenantTheme', JSON.stringify(newTheme));
    }
  };

  // Initialize tenant from localStorage on mount
  useEffect(() => {
    const savedTenantKey = localStorage.getItem('tenantKey');
    const savedTheme = localStorage.getItem('tenantTheme');
    
    if (savedTenantKey && savedTheme) {
      try {
        const parsedTheme = JSON.parse(savedTheme);
        setTenantKey(savedTenantKey);
        setTheme(parsedTheme);
        setDisplayName(parsedTheme.displayName);
        applyTheme(parsedTheme);
        
        console.log(`🔄 Restored tenant from localStorage: ${savedTenantKey}`);
      } catch (error) {
        console.error('Error parsing saved theme:', error);
        clearTenant();
      }
    } else {
      // Apply default theme
      applyTheme(defaultTheme);
    }
  }, []);

  // Listen for theme updates from login
  useEffect(() => {
    const handleThemeUpdate = (event: CustomEvent) => {
      const { tenantKey: newTenantKey, theme: newTheme } = event.detail;
      console.log('🎨 Received theme update event:', newTenantKey);
      
      setTenantKey(newTenantKey);
      setTheme(newTheme);
      setDisplayName(newTheme.displayName);
      applyTheme(newTheme);
    };

    window.addEventListener('tenantThemeUpdated', handleThemeUpdate as EventListener);
    
    return () => {
      window.removeEventListener('tenantThemeUpdated', handleThemeUpdate as EventListener);
    };
  }, []);

  const contextValue: TenantContextType = {
    tenantKey,
    displayName,
    theme,
    socketNamespace,
    isLoading,
    error,
    setTenant,
    clearTenant,
    updateTheme
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use tenant context
 */
export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;